/**
 * Stats / Social Proof Bar — Insolip brand (navy, gold borders, no "Value Commercialized")
 */
import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Lightbulb, Clock, Building2, CheckCircle2 } from 'lucide-react';
import { content } from '@/landing/data/content';

const iconMap = {
  Lightbulb,
  Clock,
  Building2,
  CheckCircle2,
};

function CountUpAnimation({ value, suffix = '', duration = 2000 }: { value: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            const startTime = Date.now();
            const endTime = startTime + duration;
            const animate = () => {
              const now = Date.now();
              const progress = Math.min((now - startTime) / duration, 1);
              const easeOut = 1 - Math.pow(1 - progress, 3);
              const currentCount = Math.floor(easeOut * value);
              setCount(currentCount);
              if (now < endTime) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.5 }
    );
    if (elementRef.current) observer.observe(elementRef.current);
    return () => observer.disconnect();
  }, [value, duration, hasAnimated]);

  return (
    <div ref={elementRef} className="text-5xl md:text-6xl font-bold font-display text-white">
      {count}{suffix}
    </div>
  );
}

export function Stats() {
  return (
    <section id="stats" className="relative py-20 bg-[var(--navy)] border-y border-[rgba(201,168,76,0.15)]">
      <div className="absolute inset-0 hero-grid-overlay opacity-60" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {content.stats.map((stat, index) => {
            const Icon = iconMap[stat.icon as keyof typeof iconMap];
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="text-center group"
              >
                <motion.div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[rgba(26,86,219,0.2)] border border-[rgba(201,168,76,0.15)] mb-4 group-hover:scale-110 transition-transform duration-300"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Icon className="w-8 h-8 text-[var(--gold-light)]" />
                </motion.div>
                <CountUpAnimation value={stat.value} suffix={stat.suffix} />
                <p className="text-[var(--text-muted)] mt-2 font-medium text-sm uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
